import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Accordion,
  AccordionDetails,
  AccordionSummary
} from "@mui/material";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SpaIcon from '@mui/icons-material/Spa';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { ArrowDropDown } from "@mui/icons-material";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { ForDeactivationIcon } from "../Commission/MimshakAgentCodeCell";
import { ProducerCommissionCompanyOverride } from "../../interfaces/DTO/commissions/producerCommission";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { formatProductType } from "../../utils/enumFormatter";
import {
  formatCurrency,
  formatHandlerCode,
  formatNumberFloor,
  formatPercentage,
} from "../../utils/namesFormater";
import CommissionListView from "../Commission/CommissionListView";
import { colorHandlerCode } from "../../utils/colorEnumFormatter";
import { AgentProducerCode } from "../../interfaces/DTO/agent/agentProducerCode";
import { FundSumDropMap } from "../../interfaces/DTO/simulation";
import { LoanChip } from "../PersonFinancialData/Chips/LoanChip";
import { Mimshak } from "../../interfaces/DTO/mislaka_xml_models";
import { isAppointedLongAfterProductJoin } from "../../utils/agentCodeOwnership";
import { MimshakProductUtil } from "../../services/MimshakProductUtil";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DragHandleIcon from "@mui/icons-material/DragHandle"; // אפשר להשתמש בזה כסימן שווה
import { lazy, Suspense } from "react";
import { ChangesDataUtils } from "./Reports/ChangesDataUtils";
import PathRatingCubes from "./PathRatingCubes";
import PathRatingMiniCubes from "./PathRatingMiniCubes";
import { findProposedFees, getAdditionTypeColor } from "../../utils/labUtils";
import { NewExposureMap } from "../../hooks/feeLab/usePathDragChanges";
import { getEffectiveSavingFee } from "../../services/userTask/simulationChangeDetectionUtil";
const InsuranceProductDrawer = lazy(() => import("../PersonFinancialData/Insurance/InsuranceProductDrawer"));
const EditExistingProductDrawer = lazy(() => import("./EditExistingProductDrawer"));
interface Props {
  handlerCode: string;
  modified_rows: MimshakData[];
  originalRowsMap?: Record<string, MimshakData>;
  modifiedRowsMap?: Record<string, MimshakData>;
  overrides: ProducerCommissionCompanyOverride[];
  onItemMoved: (item: MimshakData, toHandler: string) => void;
  onNewFeesUpdated?: (fees: Record<string, number | null>) => void;
  agentProducerCodes: AgentProducerCode[];
  fundSumDropMap: FundSumDropMap;
  selectedPeriodType: "REGULAR" | "PRE_ADVICE";
  setNewProductDrawer?: (mimshak: MimshakData) => void;
  contactColorMap?: Record<number, string>;
  onCubeClick?: (mimshakRow: MimshakData, handlerCode: string) => void;
  newExposureMap?: NewExposureMap;
  loadedManualFees?: Record<string, { fee: number; handlerCode: string }>;
  onSplitProduct?: (sourceMimshak: MimshakData, splitAmounts: number[]) => void;
  onRecurrentChange?: (policyNumber: string, amount: number | null) => void;
  onDepositChange?: (policyNumber: string, amount: number) => void;
  sortOrderMap?: Record<string, number>;
}



const FeesByProductDnD: React.FC<Props> = ({
  handlerCode,
  modified_rows: rows,
  originalRowsMap = {},
  modifiedRowsMap = {},
  overrides,
  onItemMoved,
  onNewFeesUpdated,
  agentProducerCodes,
  fundSumDropMap,
  selectedPeriodType,
  setNewProductDrawer,
  contactColorMap = {},
  onCubeClick,
  newExposureMap = {},
  loadedManualFees = {} as Record<string, { fee: number; handlerCode: string }>,
  onSplitProduct,
  onRecurrentChange,
  onDepositChange,
  sortOrderMap = {},
}) => {
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => (sortOrderMap[a.policy_number] ?? Infinity) - (sortOrderMap[b.policy_number] ?? Infinity)),
    [rows, sortOrderMap]
  );

  const [originalMap, setOriginalMap] = useState<Map<string, string>>(new Map());
  const [newSavingFees, setNewSavingFees] = useState<Record<string, number | null>>({});
  const [calculatedSavingFees, setCalculatedSavingFees] = useState<Record<string, number | null>>({});
  const [manualSavingFees, setManualSavingFees] = useState<Record<string, number | null>>({});
  const [selectedProduct, setSelectedProduct] = useState<MimshakData | null>(null);
const [editingProduct, setEditingProduct] = useState<MimshakData | null>(null);

  const totalSum = useMemo(() => {
    return rows.reduce((acc, row) => {
      const base = (modifiedRowsMap[row.policy_number]?.funds_sum ?? row.funds_sum) ?? 0;
      const additions = fundSumDropMap[row.policy_number];
      const extra = ChangesDataUtils.getTotalFundAddition(additions);
      return acc + base + extra;
    }, 0);
  }, [rows, modifiedRowsMap, fundSumDropMap]);

  useEffect(() => {
    const idToHandler = new Map<string, string>();
    rows.forEach((row) => idToHandler.set(row.policy_number, row.handler_code));
    setOriginalMap(idToHandler);
  }, []);
useEffect(() => {
  const calculated: Record<string, number | null> = {};
  const sortedOverrides = [...overrides].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  rows.forEach((row) => {
    calculated[row.policy_number] = findProposedFees(totalSum, sortedOverrides);
  });

  setCalculatedSavingFees(prev => {
    const same = JSON.stringify(prev) === JSON.stringify(calculated);
    return same ? prev : calculated;
  });
}, [rows, overrides, totalSum]);
useEffect(() => {
  // Clean up manual fees for policies that left this handler
  const currentPolicies = new Set(rows.map(r => r.policy_number));
  setManualSavingFees(prev => {
    const keys = Object.keys(prev).filter(k => !currentPolicies.has(k));
    if (keys.length === 0) return prev;
    const next = { ...prev };
    keys.forEach(k => delete next[k]);
    return next;
  });
}, [rows]);

useEffect(() => {
  if (Object.keys(loadedManualFees).length === 0) {
    setManualSavingFees({});
    return;
  }
  // Only treat a loaded fee as a manual override if:
  // 1. It was saved for this specific handler (not carried over from a different handler)
  // 2. It differs from what auto-calculation produces now (otherwise it was auto-calculated at save time)
  const onlyTrueManual: Record<string, number | null> = {};
  Object.entries(loadedManualFees).forEach(([policy, { fee, handlerCode: savedHandlerCode }]) => {
    if (savedHandlerCode === handlerCode && fee !== calculatedSavingFees[policy]) {
      onlyTrueManual[policy] = fee;
    }
  });
  setManualSavingFees(onlyTrueManual);
}, [loadedManualFees, calculatedSavingFees, handlerCode]);

useEffect(() => {
  const merged: Record<string, number | null> = {};
  rows.forEach((row) => {
    const manual = manualSavingFees[row.policy_number];
    merged[row.policy_number] =
      manual !== undefined ? manual : calculatedSavingFees[row.policy_number];
  });

  setNewSavingFees(prev => {
    const same = JSON.stringify(prev) === JSON.stringify(merged);
    if (!same) {
      onNewFeesUpdated?.(merged);
    }
    return same ? prev : merged;
  });
}, [rows, manualSavingFees, calculatedSavingFees]);

  const maxSum = Math.max(...sortedRows.map((r) => r.funds_sum ?? 0), 1);

  const agentCodeSet = useMemo(
    () => new Set(agentProducerCodes.map((code) => code.code)),
    [agentProducerCodes]
  );
  // קוד הסוכן של הפוליסה, אם מסומן כמיועד להפסקת פעילות (קודם לפי יצרן + קוד, אחרת לפי קוד בלבד – כמו agentCodeSet)
  const findForDeactivationCode = (agentCode?: string | null, rowHandlerCode?: string): AgentProducerCode | undefined => {
    if (!agentCode) return undefined;
    const code =
      agentProducerCodes.find((c) => c.code === agentCode && c.handler_code === rowHandlerCode) ??
      agentProducerCodes.find((c) => c.code === agentCode);
    return code?.for_deactivation === true ? code : undefined;
  };
  const isRecentJoinDate = (joinDateStr?: string): boolean => {
    if (!joinDateStr) return false;
    const joinDate = new Date(joinDateStr);
    if (isNaN(joinDate.getTime())) return false;
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    return joinDate >= twoYearsAgo;
  };

  const hasOldTransferInOrRecentJoinDate = (row: any): boolean => {
  const transfers = row.data?.products?.[0]?.policies_or_accounts[0].funds_transfer || [];
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    const hasRecentTransfer = transfers.some((t: any) => {
      const fixingDate = new Date(t.fixing_date);
      return !isNaN(fixingDate.getTime()) && fixingDate >= twoYearsAgo;
    });
    const joinDate = (row.data as Mimshak)?.products?.[0]?.policies_or_accounts?.[0]?.join_date;
    return hasRecentTransfer || isRecentJoinDate(joinDate);
  };
const handleManualSavingFeeChange = (policyNumber: string, newFee: number | null) => {
  const updatedManual = { ...manualSavingFees, [policyNumber]: newFee };
  setManualSavingFees(updatedManual);

  const merged: Record<string, number | null> = {
    ...calculatedSavingFees,
    ...updatedManual,
  };
  setNewSavingFees(merged);
  onNewFeesUpdated?.(merged);
};
  const handleCellClick = (row: MimshakData, column: string) => {
    const isNewPolicy = row.policy_number?.startsWith("generated-") || row.policy_number?.startsWith("transfered-");

    if (isNewPolicy) {
      const proposedFee = newSavingFees[row.policy_number] ?? null;
      setNewProductDrawer?.({ ...row, saving_fee: row.saving_fee ?? proposedFee });
    } else if (["new_saving_fee", "funds_sum"].includes(column)) {
       setEditingProduct(row);
    } else {
      setSelectedProduct(row);
    }
  };
  return (
    <Box mb={4} sx={{ position: "relative" }}>
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
      <Stack direction="row" spacing={1} alignItems="center">
        <Box width={10} height={22} borderRadius={2} bgcolor={colorHandlerCode(handlerCode)} />
        <Typography variant="h6">&nbsp; {formatHandlerCode(handlerCode)}</Typography>
      </Stack>
      <Typography variant="subtitle1" fontWeight="bold">
        {formatCurrency(totalSum)}
      </Typography>
  </Stack>

      <Droppable droppableId={handlerCode} >
        {(provided) => (
         <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
           p={1}
  borderTop={`6px solid ${colorHandlerCode(handlerCode)}`}
  borderRadius={1}
  minHeight={120}
sx={{
    background: `linear-gradient(to bottom, ${colorHandlerCode(handlerCode)}11 0%, #ffffff 60%)`,
    transition: "background-color 0.3s",
    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
    "&:hover": {
      backgroundColor: "#f9f9f9",
    },
  }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="right">מוצר</TableCell>
                  <TableCell align="right">דמי הניהול שלך</TableCell>
                  <TableCell align="right">דמי ניהול מחושבים</TableCell>
                  <TableCell align="right">סכום</TableCell>
                  <TableCell align="center" sx={{ px: 0 }}>דירוג 6M</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.map((row, index) => {
                  const newFee = newSavingFees[row.policy_number] ?? null;
                  // אנליסט / מיטב מדווחים את קוד הסוכן תמיד – לכן בודקים גם אם הסוכן מונה הרבה אחרי ההצטרפות למוצר
                  const isUnknownAgentCode = !row.agent_code || !agentCodeSet.has(row.agent_code);
                  // קוד הסוכן מוכר, אבל לפי התאריכים כנראה שהפוליסה לא ברשותך – ניחוש (אייקון אפור)
                  const isGuessedNotBelongs = !isUnknownAgentCode && isAppointedLongAfterProductJoin(row);
                  const showNotBelongsToAgent = isUnknownAgentCode || isGuessedNotBelongs;
                  // בניהולך, אבל קוד הסוכן מסומן כמיועד להפסקת פעילות (אייקון על רקע אפור)
                  const forDeactivationCode = !showNotBelongsToAgent ? findForDeactivationCode(row.agent_code, row.handler_code) : undefined;
                  const isNewPolicy = row.policy_number?.startsWith("generated-") || row.policy_number?.startsWith("transfered-");
                  const isTransferedPolicy = row.policy_number?.startsWith("transfered-");
                  const isHandlerMoved = row.handler_code !== handlerCode;
                  const manualFee = newSavingFees[row.policy_number] ?? null;
                  const calculatedFee = calculatedSavingFees[row.policy_number] ?? null;
                  const isOverridden = newFee !== null && calculatedFee !== null && newFee !== calculatedFee;
                  // effectiveFee: the fee that will actually apply — uses the same rule as all other components
                  const effectiveFee = getEffectiveSavingFee(row.saving_fee, newFee, isHandlerMoved);
                  const isLower = effectiveFee !== null && effectiveFee < (row.saving_fee ?? 0);
                  const isHigher = isHandlerMoved && newFee !== null && newFee > (row.saving_fee ?? 0);

                  const modifiedFundIds = MimshakProductUtil.getMergedPathsFromProduct(modifiedRowsMap[row.policy_number]?.data?.products?.[0]).map(p => p.path_code);
                  const originalFundSet = new Set(MimshakProductUtil.getMergedPathsFromProduct(row.data?.products?.[0]).map(p => p.path_code));
                  const hasFundChange = !!modifiedRowsMap[row.policy_number] && (
                    modifiedFundIds.some((id) => !originalFundSet.has(id)) ||
                    [...originalFundSet].some((id) => !modifiedFundIds.includes(id))
                  );

                  const isHandlerChanged = originalMap.get(row.policy_number) !== row.handler_code;
                  return (
                    <Draggable draggableId={row.policy_number} index={index} key={row.policy_number}>
                      {(dragProvided) => (
                        <TableRow
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          sx={{
                            bgcolor: isHandlerChanged ? "#fffbe6 !important" : "transparent",
                            border: isHandlerChanged ? "1px dashed orange" : "none",
                            cursor: "pointer",
                          }}
                    
                        >
                          <TableCell align="right" onClick={() => handleCellClick(row, "product_type")}>
                            <Box position="relative">
                              {/* Color triangle indicator in top right corner */}
                              {contactColorMap[row.contact_id] && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    width: 0,
                                    height: 0,
                                    borderStyle: 'solid',
                                    borderWidth: '0 8px 8px 0',
                                    borderColor: `transparent ${contactColorMap[row.contact_id]} transparent transparent`,
                                  }}
                                />
                              )}
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                              <Typography variant="body2">{formatProductType(row.product_type)}</Typography>
                              {fundSumDropMap[row.policy_number]?.recurrent === null && (originalRowsMap[row.policy_number]?.recurrent_deposit ?? 0) > 0 ? (
                                <Tooltip title={`הוראת קבע מופסקת (הייתה ${formatCurrency(originalRowsMap[row.policy_number]?.recurrent_deposit ?? 0)})`}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <PlayArrowIcon sx={{ color: 'text.disabled', fontSize: 20, textDecoration: 'line-through' }} />
                                    <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1, mt: 0.3, color: 'text.disabled', textDecoration: 'line-through' }}>M</Typography>
                                  </Box>
                                </Tooltip>
                              ) : row.recurrent_deposit && row.recurrent_deposit > 0 ? (
                                <Tooltip title={`הפקדה חודשית קבועה בסך ${formatCurrency(row.recurrent_deposit)}`}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <PlayArrowIcon sx={{ color: 'green', fontSize: 20 }} />
                                    <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1, mt: 0.3, color: 'green' }}>M</Typography>
                                  </Box>
                                </Tooltip>
                              ) : null}
                               {isNewPolicy && (
                                <>&nbsp;
                                  <Chip label={isTransferedPolicy ? "נויד" : "חדש"} color="error" size="small" />
                                  </>
                                )}
                              {showNotBelongsToAgent && !isNewPolicy && selectedPeriodType === "REGULAR" && (
                                <>
                                  &nbsp;
                                  {/* ה-Tooltip עוטף את ה-Box ישירות – על Fragment הוא לא נפתח */}
                                  <Tooltip title={isGuessedNotBelongs ? "כנראה אינך מקבל עמלה" : "אינך מקבל עמלה"}>
                                    <Box sx={{ bgcolor: "orange", borderRadius: "50%", width: 28, height: 28, minWidth: 28, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                      <PersonOffIcon fontSize="small" sx={isGuessedNotBelongs ? { color: "#757575" } : undefined} />
                                    </Box>
                                  </Tooltip>
                                </>
                              )}
                              {forDeactivationCode && !isNewPolicy && selectedPeriodType === "REGULAR" && (
                                <>
                                  &nbsp;
                                  <ForDeactivationIcon
                                    title={`מסומן להפסקת שימוש בקוד הסוכן${forDeactivationCode.description ? ` – ${forDeactivationCode.description}` : ""}`}
                                  />
                                </>
                              )}
                              {!showNotBelongsToAgent &&!isNewPolicy && hasOldTransferInOrRecentJoinDate(row) && (
                                <>
                                  &nbsp;
                                  <Tooltip title="מוצר עם ניוד נכנס פחות משנתיים">
                                       <Box
                                sx={{
                                  borderRadius: "50%",
                                  border: "2px solid green",
                                  width: 28,
                                  height: 28,
                                  minWidth: 28,
                                  flexShrink: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mt: 0.5,
                                
                                }}
                              >
                                    <SpaIcon fontSize="small" color="success" />
                                  </Box>
                                  </Tooltip>
                                </>
                                  )
                                   }
                                   <LoanChip policies={row.data?.products[0]?.policies_or_accounts} />
                            </Stack>
                            </Box>
                          </TableCell>
                          <TableCell align="right" onClick={() => handleCellClick(row, "saving_fee")}>
                            {formatPercentage(row.saving_fee)}
                          </TableCell>
               <TableCell align="right" sx={{ bgcolor: '#e3f2fd' }} onClick={() => handleCellClick(row, "new_saving_fee")}>
                <Stack spacing={0.5} alignItems="flex-end">
                  {newFee !== null ? (
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                      <Typography
                        variant="body2"
                        sx={
                          isLower
                            ? { bgcolor: "green", color: "white", px: 1, borderRadius: 1 }
                            : isHigher
                            ? { bgcolor: "#d32f2f", color: "white", px: 1, borderRadius: 1 }
                            : {}
                        }
                      >
                        {formatPercentage(newFee)}
                      </Typography>

                      {isLower && <ArrowDropDownIcon color="success" />}
                      {isHigher && <ArrowDropUpIcon color="error" />}
                      {isHandlerMoved && !isLower && !isHigher && (
                        <DragHandleIcon sx={{ color: "gray", fontSize: 20 }} />
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {overrides.length === 0 ? "?" : formatPercentage(newFee)}
                    </Typography>
                  )}

                  {/* טקסט קטן ומחוק מתחת */}
                  {isOverridden && (
                    <Typography variant="caption" sx={{ color: "gray", textDecoration: "line-through" }}>
                      {formatPercentage(calculatedFee)}
                    </Typography>
                  )}
                </Stack>
              </TableCell>
                <TableCell align="right" onClick={() => handleCellClick(row, "funds_sum")}>
                    <Droppable droppableId={`policy-${row.policy_number}`} type="fundSum">
                      {(dropProvided, snapshot) => (
                       <Box ref={dropProvided.innerRef} {...dropProvided.droppableProps} sx={{
                                  p: 1,
                                  border: snapshot.isDraggingOver ? "2px dashed #3f51b5" : undefined,
                                  borderRadius: 1,
                                  minHeight: 5,
                                  backgroundColor: snapshot.isDraggingOver ? "#f0f4ff" : undefined,
                                  textAlign: "center"
                                }}>
                          {(() => {
                            const originalFundsSum = originalRowsMap[row.policy_number]?.funds_sum;
                            const currentFundsSum = modifiedRowsMap[row.policy_number]?.funds_sum ?? row.funds_sum;
                            const hasFundsSumChange = originalFundsSum !== undefined && originalFundsSum !== currentFundsSum;

                            if (hasFundsSumChange) {
                              return (
                                <Stack spacing={0} alignItems="center">
                                  <Typography variant="caption" sx={{ textDecoration: "line-through", color: "text.secondary" }}>
                                    {formatNumberFloor(originalFundsSum)}
                                  </Typography>
                                  <Typography>{formatNumberFloor(currentFundsSum)}</Typography>
                                </Stack>
                              );
                            }
                            return <Typography>{formatNumberFloor(currentFundsSum)}</Typography>;
                          })()}
                           {snapshot.isDraggingOver && (
                                    <Typography variant="caption" color="primary">סכום נוסף</Typography>
                                  )}
                          {(() => {
                            const additions = fundSumDropMap[row.policy_number];
                            if (!additions) return null;

                            const chips = [];
                            let chipIndex = 0;

                            // Chip for deposit
                            if (additions.deposit) {
                              chips.push(
                                <Draggable key="deposit" draggableId={`fundSum-${additions.deposit}-deposit-from-${row.policy_number}`} index={1000 + index + chipIndex++}>
                                  {(chipProvided) => (
                                    <div
                                      ref={chipProvided.innerRef}
                                      {...chipProvided.draggableProps}
                                      {...chipProvided.dragHandleProps}
                                      style={{ ...chipProvided.draggableProps.style, display: 'inline-block', marginLeft: 4 }}
                                    >
                                      <Chip
                                        label={`${formatNumberFloor(additions.deposit)}+`}
                                        size="small"
                                        sx={{
                                          backgroundColor: getAdditionTypeColor('deposit'),
                                          color: 'white',
                                          fontWeight: 'bold'
                                        }}
                                      />
                                    </div>
                                  )}
                                </Draggable>
                              );
                            }

                            // Chip for recurrent
                            if (additions.recurrent === null) {
                              chips.push(
                                <Chip
                                  key="recurrent-stopped"
                                  label="הפסקת הוראת קבע"
                                  size="small"
                                  sx={{ backgroundColor: '#E17055', color: 'white', fontWeight: 'bold' }}
                                />
                              );
                            } else if (additions.recurrent) {
                              chips.push(
                                <Draggable key="recurrent" draggableId={`fundSum-${additions.recurrent}-recurrent-from-${row.policy_number}`} index={1000 + index + chipIndex++}>
                                  {(chipProvided) => (
                                    <div
                                      ref={chipProvided.innerRef}
                                      {...chipProvided.draggableProps}
                                      {...chipProvided.dragHandleProps}
                                      style={{ ...chipProvided.draggableProps.style, display: 'inline-block', marginLeft: 4 }}
                                    >
                                      <Chip
                                        label={`M${formatNumberFloor(additions.recurrent)}+`}
                                        size="small"
                                        sx={{
                                          backgroundColor: getAdditionTypeColor('recurrent'),
                                          color: 'white',
                                          fontWeight: 'bold'
                                        }}
                                      />
                                    </div>
                                  )}
                                </Draggable>
                              );
                            }

                            // Chip for transfer
                            if (additions.transfer) {
                              chips.push(
                                <Draggable key="transfer" draggableId={`fundSum-${additions.transfer}-transfer-from-${row.policy_number}`} index={1000 + index + chipIndex++}>
                                  {(chipProvided) => (
                                    <div
                                      ref={chipProvided.innerRef}
                                      {...chipProvided.draggableProps}
                                      {...chipProvided.dragHandleProps}
                                      style={{ ...chipProvided.draggableProps.style, display: 'inline-block', marginLeft: 4 }}
                                    >
                                      <Chip
                                        label={`T${formatNumberFloor(additions.transfer)}+`}
                                        size="small"
                                        sx={{
                                          backgroundColor: getAdditionTypeColor('transfer'),
                                          color: 'white',
                                          fontWeight: 'bold'
                                        }}
                                      />
                                    </div>
                                  )}
                                </Draggable>
                              );
                            }

                            // Chip for withdrawal
                            if (additions.withdrawal) {
                              chips.push(
                                <Draggable key="withdrawal" draggableId={`fundSum-${additions.withdrawal}-withdrawal-from-${row.policy_number}`} index={1000 + index + chipIndex++}>
                                  {(chipProvided) => (
                                    <div
                                      ref={chipProvided.innerRef}
                                      {...chipProvided.draggableProps}
                                      {...chipProvided.dragHandleProps}
                                      style={{ ...chipProvided.draggableProps.style, display: 'inline-block', marginLeft: 4 }}
                                    >
                                      <Chip
                                        label={`${formatNumberFloor(additions.withdrawal)}-`}
                                        size="small"
                                        sx={{
                                          backgroundColor: getAdditionTypeColor('withdrawal'),
                                          color: 'white',
                                          fontWeight: 'bold'
                                        }}
                                      />
                                    </div>
                                  )}
                                </Draggable>
                              );
                            }

                            return chips.length > 0 ? (
                              <Box mt={0.5} display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                                {chips}
                              </Box>
                            ) : null;
                          })()}
                          {dropProvided.placeholder}
                        </Box>
                      )}
                    </Droppable>
                </TableCell>
                <TableCell align="center" sx={{ px: 0, bgcolor: hasFundChange ? "#fffbe6 !important" : undefined }}>
                  <PathRatingMiniCubes
                    mimshakRow={row}
                    onCubeClick={onCubeClick ? (mimshakRow) => onCubeClick(mimshakRow, handlerCode) : undefined}
                    exposureChanges={newExposureMap[row.policy_number]}
                    handlerCode={handlerCode}
                    modifiedFundIds={modifiedFundIds.length ? modifiedFundIds : undefined}
                  />
                </TableCell>


                        </TableRow>
                      )}
                    </Draggable>
                  );
                })}
              </TableBody>
            </Table>
            {provided.placeholder}
           <Droppable droppableId={`newPolicy-${handlerCode}`} type="fundSum">
            {(zoneProvided, zoneSnapshot) => (
             <Box
                ref={zoneProvided.innerRef}
                {...zoneProvided.droppableProps}
                mt={0}
                minHeight={10}
                border="2px dashed #ccc"
                borderRadius={2}
                textAlign="center"
                color="gray"
                sx={{
                  visibility: zoneSnapshot.isDraggingOver ? "visible" : "hidden",
                  backgroundColor: zoneSnapshot.isDraggingOver ? "#f5f5f5" : "transparent",
                }}
              >
                גרור סכום לכאן ליצירת מוצר חדש
                {zoneProvided.placeholder}
              </Box>
            )}
          </Droppable>
          </Box>
        )}
      </Droppable>

    
{/* <PathRatingCubes data={rows} /> */}
     

  {/* <CommissionSliderView
        modified_rows={overrides}
        globalMax={maxSum}
        currentSum={totalSum}
      /> */}
     <Accordion>
  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
    <Typography variant="subtitle1" fontWeight="bold">
      מדרגות דמי ניהול
    </Typography>
  </AccordionSummary>
  <AccordionDetails>
    <CommissionListView rows={overrides} currentSum={totalSum} />
  </AccordionDetails>
</Accordion>
  
  <Suspense fallback={null}>

      {selectedProduct && (
        <InsuranceProductDrawer
          product={selectedProduct.data.products[0]}
          onClose={() => setSelectedProduct(null)}
        />
      )}
   </Suspense> 
   <Suspense fallback={null}>
  {editingProduct && (
<EditExistingProductDrawer
open={!!editingProduct}
mimshak={editingProduct}
originalFee={calculatedSavingFees[editingProduct?.policy_number ?? ""] ?? null}
currentFee={newSavingFees[editingProduct?.policy_number ?? ""] ?? null}
onManualSavingFeeChange={handleManualSavingFeeChange}
onClose={() => setEditingProduct(null)}
onSplitProduct={onSplitProduct}
currentDeposit={fundSumDropMap[editingProduct?.policy_number ?? ""]?.deposit ?? 0}
onDepositChange={onDepositChange}
currentRecurrent={fundSumDropMap[editingProduct?.policy_number ?? ""]?.recurrent !== undefined ? fundSumDropMap[editingProduct?.policy_number ?? ""]?.recurrent : 0}
onRecurrentChange={onRecurrentChange}
/>
  )}
</Suspense>  
    </Box>

  );
};

export default FeesByProductDnD;
